import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardFooter,
  Heading,
  HStack,
  Icon,
  LinkBox,
  LinkOverlay,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Skeleton,
  Text,
  Tooltip,
  VStack,
  Badge,
  ButtonGroup,
  IconButton,
  Input,
  Flex,
} from "@chakra-ui/react";
import {
  RiArrowDownSLine,
  RiArrowRightLine,
  RiCloseLine,
  RiFileAddLine,
} from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { formatDistance } from "date-fns";
import { useEffect, useState } from "react";
import FeatherIcon from "../../../components/UI/Icon";
import ChakraScreenContainer from "../../../components/chakra/ChakraScreenContainer";
import { useApiQuery } from "../../../utilities/apibelRequest";
import DescriptionField from "../../../components/chakra/DescriptionField";
import {
  ObjectTypeGroupNametoID,
  ObjectTypeGroupIDToName,
} from "../../../shared/constants/objectTypeGroup";

import { Select } from "../../../components/Select";

import {
  Register,
  Requirement,
  Action,
  Issue,
  Risk,
} from "../../../constants/objectTypeGroup";
import { useAppState } from "../../../components/App/AppProvider";
import { getLimitFor, hasAccessTo } from "../../../shared/_core/limit.utils";

type ReportData = {
  reportID: string;
  reportReadableID: string;
  name: string;
  createTs: Date;
  modifiedTs: Date;
  createUserID: string;
  modifiedUserID: string;
  description: string;
  objectTypeID: string;
  responsibleUser: {
    userID: string;
    firstName: string;
    lastName: string;
  };
};
const ReportDashboardScreen = () => {
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const objectTypeID = urlParams.get("filter");
  const { app } = useAppState();
  const { permissions } = app;

  const hasAccessToRisk = hasAccessTo("risk", app.license);

  const canViewTimeReport =
    permissions.TIME_NOTES.READ.ALL || !permissions.REPORTS.READ.ALL;
  const canViewAlertHistoryReport = permissions.REPORTS.READ.ALL;
  const ReportTypeSelectOptions = [
    { value: Action, label: ObjectTypeGroupIDToName[Action] },
    { value: Issue, label: ObjectTypeGroupIDToName[Issue] },
    { value: Register, label: ObjectTypeGroupIDToName[Register] },
    { value: Requirement, label: ObjectTypeGroupIDToName[Requirement] },
    ...(hasAccessToRisk
      ? [{ value: Risk, label: ObjectTypeGroupIDToName[Risk] }]
      : []),
    { value: "Quick Reports", label: "Quick Reports" },
  ];
  const defaultQuickReports = [
    {
      name: "Time Sheet Report",
      reportLink: "/report/easy/time-sheet",
      icon: "Clock",
    },
    {
      name: "Alert Right Now History Report",
      reportLink: "/report/easy/alert-history",
      icon: "Archive",
    },
  ];

  const showCreateReport =
    app.permissions.REPORTS.CREATE.ALL || app.permissions.REPORTS.CREATE.SELF;
  const canCreateAdditionalReports = getLimitFor("report::count", app.license);

  const reportsQuery = useApiQuery("report/getManyReports", null);
  const reportData = reportsQuery.data;
  const { isError, isSuccess } = reportsQuery;
  const [inputQuery, setInputQuery] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState("is");
  const [groupedReports, setGroupedReports] =
    useState<Record<string, ReportData[]>>();
  const [listValue, setListValue] = useState<
    {
      value: string;
      label: string;
    }[]
  >(ReportTypeSelectOptions);
  const [quickReports, setQuickReports] = useState<
    {
      name: string;
      reportLink: string;
      icon: string;
    }[]
  >(defaultQuickReports);

  const getIcon = (type: string) => {
    const iconMap: Record<string, string> = {
      Requirement: "FileText",
      Action: "Edit",
      Issue: "AlertCircle",
      Risk: "AlertTriangle",
      Job: "Briefcase",
      Register: "Layers",
    };
    return iconMap[type] || "File";
  };

  const reportTypes = [
    {
      objectTypeID: ObjectTypeGroupNametoID.Action,
      name: "Action",
      icon: getIcon("Action"),
    },
    {
      objectTypeID: ObjectTypeGroupNametoID.Issue,
      name: "Issue",
      icon: getIcon("Issue"),
    },
    {
      objectTypeID: ObjectTypeGroupNametoID.Register,
      name: "Register",
      icon: getIcon("Register"),
    },
    {
      objectTypeID: ObjectTypeGroupNametoID.Requirement,
      name: "Requirement",
      icon: getIcon("Requirement"),
    },

    {
      objectTypeID: ObjectTypeGroupNametoID.Risk,
      name: "Risk",
      icon: getIcon("Risk"),
    },
  ];

  const filteredQuickReports = quickReports.filter((report) =>
    report.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const applySearch = (filteredResults, quickReportsResults) => {
    if (!searchQuery.trim()) {
      setGroupedReports(
        filteredResults.reduce(
          (acc, report) => {
            acc[report.objectTypeID] = acc[report.objectTypeID] || [];
            acc[report.objectTypeID].push(report);
            return acc;
          },
          {} as Record<string, ReportData[]>,
        ),
      );
      setQuickReports(quickReportsResults);
      return;
    }
    const query = searchQuery.toLowerCase();

    const searchFilteredResults = filteredResults.filter(
      (report) =>
        report.name.toLowerCase().includes(query) ||
        report.reportReadableID.toLowerCase().includes(query),
    );

    const searchFilteredQuickReports = quickReportsResults.filter((report) =>
      report.name.toLowerCase().includes(query),
    );

    if (
      searchFilteredResults.length === 0 &&
      searchFilteredQuickReports.length === 0
    ) {
      setGroupedReports({});
      setQuickReports([]);
      return;
    }

    setGroupedReports(
      searchFilteredResults.reduce(
        (acc, report) => {
          acc[report.objectTypeID] = acc[report.objectTypeID] || [];
          acc[report.objectTypeID].push(report);
          return acc;
        },
        {} as Record<string, ReportData[]>,
      ),
    );

    setQuickReports(searchFilteredQuickReports);
  };

  const applyFilter = () => {
    if (!reportData) {
      setGroupedReports({});
      setQuickReports([]);
      return;
    }

    let filteredResults = [...reportData.report];
    let quickReportsResults = [...defaultQuickReports];

    // permission check on quick reports
    if (!canViewTimeReport) {
      quickReportsResults = quickReportsResults.filter(
        (report) => report.name !== "Time Sheet Report",
      );
    }
    if (!canViewAlertHistoryReport) {
      quickReportsResults = quickReportsResults.filter(
        (report) => report.name !== "Alert Right Now History Report",
      );
    }

    const listValuesArray = listValue.map((item) => String(item.value));

    if (selectedFilter) {
      filteredResults = filteredResults.filter((report) =>
        selectedFilter === "is"
          ? listValuesArray.includes(String(report.objectTypeID))
          : !listValuesArray.includes(String(report.objectTypeID)),
      );
      quickReportsResults = quickReportsResults.filter(() =>
        selectedFilter === "is"
          ? listValuesArray.includes("Quick Reports")
          : !listValuesArray.includes("Quick Reports"),
      );
    }
    // sort alphabetically by object type
    filteredResults = filteredResults.sort((a, b) => {
      return ObjectTypeGroupIDToName[a.objectTypeID].localeCompare(
        ObjectTypeGroupIDToName[b.objectTypeID],
      );
    });
    applySearch(filteredResults, quickReportsResults);
  };

  const handleClearFilter = () => {
    setListValue(ReportTypeSelectOptions);
    setSelectedFilter("is");
    applyFilter();
  };
  const handleSearch = () => {
    const trimmedQuery = inputQuery.trim();

    if (!trimmedQuery) {
      setSearchQuery("");
      applyFilter();
      return;
    }

    setSearchQuery(trimmedQuery);
    applyFilter();
  };
  useEffect(() => {
    if (reportsQuery.data) {
      applyFilter();
    }
  }, [reportsQuery.data, selectedFilter, listValue, searchQuery]);
  useEffect(() => {
    if (objectTypeID) {
      const matchedType = ReportTypeSelectOptions.find(
        (option) => String(option.value) === objectTypeID,
      );

      if (matchedType) {
        setListValue([matchedType]);
      }
    } else {
      setListValue(ReportTypeSelectOptions);
    }

    applyFilter();
  }, [objectTypeID]);

  const buttonProps = {
    borderLeftWidth: "1px",
    borderRightWidth: "1px",
    borderColor: "gray.200",
    color: "blackAlpha.700",
  };

  return (
    <chakra-scope>
      <ChakraScreenContainer
        headerEndContent={
          <Menu placement="bottom">
            {reportTypes && (
              <>
                {showCreateReport && (
                  <MenuButton
                    size="sm"
                    as={Button}
                    isDisabled={!canCreateAdditionalReports}
                    leftIcon={<Icon as={RiFileAddLine} />}
                    rightIcon={<Icon as={RiArrowDownSLine} />}>
                    Create New Report
                  </MenuButton>
                )}
                <MenuList minH="48px">
                  {reportTypes.map(({ objectTypeID, name, icon }) => (
                    <MenuItem
                      key={objectTypeID}
                      onClick={() =>
                        navigate(`/report/new?type=${objectTypeID}`)
                      }>
                      <HStack>
                        <FeatherIcon size="md" name={icon} />
                        <Text>{name}</Text>
                      </HStack>
                    </MenuItem>
                  ))}
                </MenuList>
              </>
            )}
          </Menu>
        }
        contentSize="container.2xl"
        pageTitle={
          objectTypeID
            ? `${ObjectTypeGroupIDToName[objectTypeID]} Reports`
            : "Reports"
        }
        breadcrumbs={[]}>
        {isSuccess ? (
          // filter stuff here
          <>
            <VStack align="start" spacing={4} mb={4}>
              <HStack w="full" maxW="96">
                <Input
                  onChange={(e) => setInputQuery(e.target.value)}
                  size="sm"
                  placeholder="Search by name or id"
                />
                <Button
                  size="sm"
                  type="submit"
                  onClick={handleSearch}
                  isDisabled={false}>
                  Search
                </Button>
              </HStack>
              <Flex display="flex" flexWrap="wrap" rowGap="1" columnGap="2">
                <ButtonGroup
                  size="sm"
                  isAttached
                  colorScheme="gray"
                  {...buttonProps}>
                  <Button variant="outline" {...buttonProps} onClick={() => {}}>
                    Report Type
                  </Button>
                  <Select
                    w="full"
                    singleOptionsDisplayMode="radio"
                    value={selectedFilter}
                    onChange={(e) => {
                      setSelectedFilter(e as string);
                      applyFilter();
                    }}
                    getKey={(option) => option}
                    options={["is", "is not"]}
                    closeOnSelect
                    type="simple-button"
                    placeholderText={selectedFilter === "is" ? "is" : "is not"}
                    simpleButtonProps={buttonProps}
                  />
                  <Select
                    multiselect
                    closeOnSelect
                    type="simple-button"
                    w="full"
                    options={ReportTypeSelectOptions}
                    value={listValue}
                    onChange={(newVal) => {
                      setListValue(newVal);
                    }}
                    renderOptionWithWrapper
                    renderOption={(renderProps) => (
                      <Text>{renderProps.option.label}</Text>
                    )}
                    placeholderText={
                      listValue && listValue.length === 0 ? (
                        <Text color="blackAlpha.700" />
                      ) : listValue.length <= 1 ? (
                        <Text color="blackAlpha.700">{listValue[0].label}</Text>
                      ) : listValue.length <= 4 ? (
                        <Text color="blackAlpha.700">
                          any of (
                          {listValue.map((item) => item.label).join(", ")})
                        </Text>
                      ) : (
                        <Text color="blackAlpha.700">
                          any of ({listValue.length} items)
                        </Text>
                      )
                    }
                  />

                  <Tooltip label="Clear Filter">
                    <IconButton
                      onClick={handleClearFilter}
                      aria-label="Clear Filter"
                      borderLeftWidth="1px"
                      borderColor="gray.200"
                      icon={
                        <Icon boxSize="5" color="red.700" as={RiCloseLine} />
                      }
                    />
                  </Tooltip>
                </ButtonGroup>
              </Flex>
            </VStack>
            {groupedReports &&
            Object.entries(groupedReports).length === 0 &&
            filteredQuickReports.length === 0 ? (
              <Text>No reports found</Text>
            ) : null}
            <Accordion allowToggle allowMultiple>
              {groupedReports &&
                Object.entries(groupedReports).map(([reportType, reports]) => (
                  <AccordionItem key={reportType}>
                    <h2>
                      <AccordionButton>
                        <Box
                          flex="1"
                          textAlign="left"
                          display="flex"
                          alignItems="center">
                          <FeatherIcon
                            size="md"
                            name={getIcon(ObjectTypeGroupIDToName[reportType])}
                          />
                          <Text ml="2" fontWeight="bold">
                            {ObjectTypeGroupIDToName[reportType]} Reports
                          </Text>
                        </Box>
                        <Text ml="2" fontSize="sm" color="gray.500">
                          {reports.length} reports
                        </Text>
                        <AccordionIcon />
                      </AccordionButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <SimpleGrid columns={[2, 3, 4]} spacing={4}>
                        {reports.map(
                          ({
                            reportID,
                            reportReadableID,
                            name,
                            description,
                            responsibleUser,
                            modifiedTs,
                          }) => (
                            <LinkBox
                              _hover={{
                                boxShadow: "sm",
                                borderColor: `${"brand.500"} `,
                                "& .arrow-icon": {
                                  transform: "translate(3px, 0)",
                                  color: `${"brand.500"}`,
                                  opacity: 1.0,
                                },
                                "& .heading-text": {
                                  color: `${"brand.500"}`,
                                },
                              }}
                              as={Card}
                              variant="outline">
                              <CardHeader>
                                <LinkOverlay
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  as={Link}
                                  to={`/report/${reportID}`}>
                                  <Heading
                                    transitionProperty="common"
                                    transitionDuration="fast"
                                    className="heading-text"
                                    size="xs"
                                    alignItems="center"
                                    display="flex">
                                    <Badge colorScheme="gray">
                                      {reportReadableID}
                                    </Badge>{" "}
                                    - {name}
                                  </Heading>

                                  <Icon
                                    transitionProperty="common"
                                    transitionDuration="fast"
                                    color="black"
                                    opacity="0.0"
                                    ml="1"
                                    boxSize="5"
                                    transition=""
                                    className="arrow-icon"
                                    as={RiArrowRightLine}
                                  />
                                </LinkOverlay>
                                <HStack>
                                  <Tooltip
                                    label={`${responsibleUser.firstName} ${responsibleUser.lastName}`}>
                                    <AvatarGroup size="sm" max={2}>
                                      <Avatar
                                        name={`${responsibleUser.firstName} ${responsibleUser.lastName}`}
                                      />
                                    </AvatarGroup>
                                  </Tooltip>
                                </HStack>
                              </CardHeader>
                              <CardBody>
                                <DescriptionField description={description} />
                              </CardBody>
                              <CardFooter>
                                <Text fontSize="xs" color="gray.500">
                                  Last Modified:{" "}
                                  {formatDistance(
                                    new Date(modifiedTs),
                                    new Date(),
                                  )}{" "}
                                  ago
                                </Text>
                              </CardFooter>
                            </LinkBox>
                          ),
                        )}
                      </SimpleGrid>
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              {/* need permission check for these reports */}
              {filteredQuickReports.length === 0 ? null : (
                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box
                        flex="1"
                        textAlign="left"
                        display="flex"
                        alignItems="center">
                        <FeatherIcon size="md" name="BarChart2" />
                        <HStack spacing={2}>
                          <Text ml="2" fontWeight="bold">
                            Quick Reports{" "}
                          </Text>{" "}
                          <Badge>System Built</Badge>
                        </HStack>
                      </Box>
                      <Text ml="2" fontSize="sm" color="gray.500">
                        {filteredQuickReports.length} reports
                      </Text>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4} />
                  <AccordionPanel pb={4}>
                    <SimpleGrid columns={[2, 3, 4]} spacing={4}>
                      {filteredQuickReports.map(
                        ({ name, reportLink, icon }) => (
                          <LinkBox
                            _hover={{
                              boxShadow: "sm",
                              borderColor: `${"brand.500"} `,
                              "& .arrow-icon": {
                                transform: "translate(3px, 0)",
                                color: `${"brand.500"}`,
                                opacity: 1.0,
                              },
                              "& .heading-text": {
                                color: `${"brand.500"}`,
                              },
                            }}
                            as={Card}
                            variant="outline">
                            <CardHeader>
                              <LinkOverlay
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                                as={Link}
                                to={reportLink}>
                                <Heading
                                  transitionProperty="common"
                                  transitionDuration="fast"
                                  className="heading-text"
                                  size="xs"
                                  alignItems="center"
                                  display="flex">
                                  <Icon as={FeatherIcon} name={icon} mr={2} />{" "}
                                  <Text>{name}</Text>
                                </Heading>

                                <Icon
                                  transitionProperty="common"
                                  transitionDuration="fast"
                                  color="black"
                                  opacity="0.0"
                                  ml="1"
                                  boxSize="5"
                                  transition=""
                                  className="arrow-icon"
                                  as={RiArrowRightLine}
                                />
                              </LinkOverlay>
                            </CardHeader>
                          </LinkBox>
                        ),
                      )}
                    </SimpleGrid>
                  </AccordionPanel>
                </AccordionItem>
              )}
            </Accordion>
          </>
        ) : isError ? (
          <Text>Something went wrong</Text>
        ) : (
          <SimpleGrid columns={[2, 3, 4]} spacing={4}>
            <Skeleton height="60" />
            <Skeleton height="60" />
            <Skeleton height="60" />
            <Skeleton height="60" />
          </SimpleGrid>
        )}
      </ChakraScreenContainer>
    </chakra-scope>
  );
};

export default ReportDashboardScreen;
